import { useMemo } from 'react'

import { useTranslation } from 'react-i18next'

import { AlertCircleIcon, Box, Flex, Spinner, tokenVar } from '@hivewatch/dark'
import type { ColorScheme } from '@hivewatch/dark'

import { VirusScanStatus } from '@/graphql/generated/schemas'

import { SOPType } from '../types/types'
import { SOPStatusBox } from './SOPStatusBox'

export const SOPDocument = ({
  sop,
  isLoading,
  colorScheme = 'light',
}: {
  sop: SOPType
  isLoading: boolean
  colorScheme?: ColorScheme
}) => {
  const { t } = useTranslation()
  const colorPrefix = colorScheme === 'light' ? 'light.' : ''
  const errorIconColor =
    colorScheme === 'light'
      ? tokenVar('colors.light.status.error')
      : tokenVar('colors.status.error')
  const status = sop?.status

  // Extract base URL without S3 signature parameters
  const baseUrl = useMemo(() => {
    if (!sop?.url) return ''
    return sop.url.split('?')[0]
  }, [sop?.url])

  // Only recalculate when base URL changes to prevent iframe reloads on signature refresh
  const pdfUrl = useMemo(
    () => (sop?.url ? `${sop.url}#toolbar=1&navpanes=0&view=FitH` : ''),
    [baseUrl]
  )

  // Still fetching the SOP record itself — no metadata yet, so no title to show.
  if (isLoading && !sop) {
    return (
      <Flex alignItems='center' h='100%' justifyContent='center'>
        <Spinner
          borderWidth='4px'
          color={`${colorPrefix}text.link`}
          colorScheme={colorScheme}
          data-testid='SOPDocument:spinner'
          size='lg'
        />
      </Flex>
    )
  }
  // SOP record exists in cache but the backend is still scanning the file.
  if (status === VirusScanStatus.Unscanned) {
    return (
      <SOPStatusBox title={t('sops:drawer_uploading')}>
        <Spinner
          borderWidth='4px'
          color={`${colorPrefix}text.link`}
          colorScheme={colorScheme}
          data-testid='SOPDocument:spinner'
          size='lg'
        />
      </SOPStatusBox>
    )
  }
  if (status === VirusScanStatus.Infected) {
    return (
      <SOPStatusBox title={t('sops:drawer_upload_failed')}>
        <AlertCircleIcon color={errorIconColor} size={54} />
      </SOPStatusBox>
    )
  }

  return (
    <Box h='100%' w='100%'>
      <iframe
        data-testid='SOPDocument:iframe'
        src={pdfUrl}
        style={{
          border: 'none',
          display: 'block',
          height: '100%',
          width: '100%',
        }}
        title={t('sops:drawer_sop_document_title')}
      />
    </Box>
  )
}
